import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'

import { get, put } from '@/lib/api'
import {
  useEfficiencyStore,
  defaultPreferences,
  type EfficiencyPreferences,
} from '@/stores/efficiency.store'

// ==================== Query Keys ====================

export const efficiencyPreferencesKeys = {
  all: ['efficiency-preferences'] as const,
}

// ==================== Types ====================

interface UpdatePreferencesInput {
  enableQuickEntry?: boolean
  enableSuggestions?: boolean
  enableShortcuts?: boolean
  enableDraftAutoSave?: boolean
  enableNotificationDigest?: boolean
  timelogReminderTime?: string
  defaultTimelogHours?: number
  dailyTimelogGoal?: number
  doNotDisturb?: boolean
  customShortcuts?: Record<string, string>
}

// ==================== Hooks ====================

/**
 * 获取效率偏好设置
 */
export function useEfficiencyPreferences() {
  const { preferences, setPreferences } = useEfficiencyStore()

  const query = useQuery({
    queryKey: efficiencyPreferencesKeys.all,
    queryFn: () => get<EfficiencyPreferences>('/users/me/efficiency-preferences'),
    staleTime: 300000, // 5 minutes
  })

  // Sync server data to local store
  useEffect(() => {
    if (query.data) {
      setPreferences(query.data)
    }
  }, [query.data, setPreferences])

  return {
    ...query,
    data: query.data ?? preferences ?? defaultPreferences,
  }
}

/**
 * 更新效率偏好设置
 */
export function useUpdateEfficiencyPreferences() {
  const queryClient = useQueryClient()
  const { setPreferences, preferences } = useEfficiencyStore()

  return useMutation({
    mutationFn: (input: UpdatePreferencesInput) =>
      put<EfficiencyPreferences>('/users/me/efficiency-preferences', input),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: efficiencyPreferencesKeys.all })

      // Save for rollback
      const previousPreferences = preferences

      // Optimistic update
      if (preferences) {
        setPreferences({ ...preferences, ...input })
      }

      return { previousPreferences }
    },
    onSuccess: (data) => {
      setPreferences(data)
      void queryClient.invalidateQueries({ queryKey: efficiencyPreferencesKeys.all })
    },
    onError: (_err, _input, context) => {
      // Rollback on error
      if (context?.previousPreferences) {
        setPreferences(context.previousPreferences)
      }
    },
  })
}

/**
 * 获取单个偏好设置值
 */
export function usePreference<K extends keyof EfficiencyPreferences>(
  key: K
): EfficiencyPreferences[K] {
  const { data } = useEfficiencyPreferences()
  return data[key]
}

/**
 * 检查功能是否启用
 */
export function useIsFeatureEnabled(
  feature: 'quickEntry' | 'suggestions' | 'shortcuts' | 'draftAutoSave' | 'notificationDigest'
): boolean {
  const { data } = useEfficiencyPreferences()

  switch (feature) {
    case 'quickEntry':
      return data.enableQuickEntry
    case 'suggestions':
      return data.enableSuggestions
    case 'shortcuts':
      return data.enableShortcuts
    case 'draftAutoSave':
      return data.enableDraftAutoSave
    case 'notificationDigest':
      return data.enableNotificationDigest
    default:
      return true
  }
}

/**
 * 获取默认工时数
 */
export function useDefaultTimelogHours(): number {
  const { data } = useEfficiencyPreferences()
  return data.defaultTimelogHours ?? 2.0
}

/**
 * 获取每日工时目标
 */
export function useDailyTimelogGoal(): number {
  const { data } = useEfficiencyPreferences()
  return data.dailyTimelogGoal ?? 8.0
}

/**
 * 检查是否处于勿扰模式
 */
export function useDoNotDisturb(): boolean {
  const { data } = useEfficiencyPreferences()
  return data.doNotDisturb
}
